import type { MouseEvent } from 'react';

import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Checkbox,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
} from '@chakra-ui/react';

import { ProductCatalogV2, ProductSlug } from '../../../../gql/generated/graphql';

import { ProductAvailability, getProductShortLabel } from '../../../../types/products';

type ProductSelectorProps = {
  products: ProductCatalogV2[];
  selectedSlugs: ProductSlug[];
  onToggle: (slug: ProductSlug) => void;
  getAvailability: (slug: ProductSlug) => ProductAvailability;
  isDisabled?: boolean;
};

const ProductSelector = ({
  products,
  selectedSlugs,
  onToggle,
  getAvailability,
  isDisabled = false,
}: ProductSelectorProps) => {
  // Generate display text for the button using short labels
  const getDisplayText = () => {
    if (selectedSlugs.length === 0) {
      return 'None';
    }

    const selectedLabels = products
      .filter((p) => selectedSlugs.includes(p.slug))
      .map((p) => getProductShortLabel(p.slug));

    return selectedLabels.join(', ');
  };

  // Handle clicking on a product checkbox
  const handleProductClick = (e: MouseEvent<HTMLButtonElement>, slug: ProductSlug) => {
    e.preventDefault();
    e.stopPropagation();
    onToggle(slug);
  };

  return (
    <Menu closeOnSelect={false}>
      <MenuButton
        as={Button}
        size="xs"
        variant="outline"
        rightIcon={<ChevronDownIcon />}
        isDisabled={isDisabled}
        fontWeight="normal"
        fontSize="xs"
        px={2}
        h="auto"
        minH="24px"
        whiteSpace="normal"
        textAlign="left"
        lineHeight="short"
        py={1}
        minW="80px"
        maxW="120px"
        bg="white"
        borderColor="gray.300"
        _hover={{ borderColor: 'gray.400' }}
        _expanded={{ borderColor: 'blue.500' }}
      >
        <Text fontSize="xs" whiteSpace="normal" lineHeight="short">
          {getDisplayText()}
        </Text>
      </MenuButton>
      <MenuList zIndex="dropdown" fontSize="sm" minW="150px" py={1}>
        {products.map((product) => {
          const isSelected = selectedSlugs.includes(product.slug);
          const availability = getAvailability(product.slug);
          const isUnavailable = !availability.available;
          const canToggle = !isUnavailable || isSelected;
          const shortLabel = getProductShortLabel(product.slug);

          const menuItem = (
            <MenuItem
              key={product.slug}
              onClick={(e) => handleProductClick(e, product.slug)}
              isDisabled={!canToggle}
              _hover={{ bg: canToggle ? 'gray.100' : 'transparent' }}
              cursor={canToggle ? 'pointer' : 'not-allowed'}
              opacity={canToggle ? 1 : 0.5}
              py={1}
              px={3}
            >
              <Checkbox
                isChecked={isSelected}
                isDisabled={!canToggle}
                size="sm"
                mr={2}
                pointerEvents="none"
                colorScheme="blue"
              >
                <Text fontSize="sm" color={!canToggle ? 'gray.400' : 'inherit'}>
                  {shortLabel}
                </Text>
              </Checkbox>
            </MenuItem>
          );

          // Wrap unavailable items in a tooltip
          if (isUnavailable && availability.reason) {
            return (
              <Tooltip key={product.slug} label={availability.reason} placement="right" hasArrow>
                <Box>{menuItem}</Box>
              </Tooltip>
            );
          }

          return menuItem;
        })}
      </MenuList>
    </Menu>
  );
};

export default ProductSelector;
